<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

import CloseIcon from '@/assets/svg/close-24.svg?component';
import GetHelpContent from '@/components/Shared/AppBar/GetHelp/GetHelpContent.vue';
import { useHelpButton } from '@/composables/useHelpButton';
import { DrawerService } from '@/core/shared/drawer/drawer.service';
import { helpCenterUrl } from '@/core/shared/help-center/help-center-url';

const drawerService = new DrawerService();
const { helpContext } = useHelpButton();

const isOpen = computed({
  get: () => drawerService.isHelpDrawerOpen,
  set: (value: boolean) => (drawerService.isHelpDrawerOpen = value),
});

// The help page for the screen the drawer was opened from. Reassigning the src (and
// bumping the key) takes the frame back there after the user has clicked around.
const pageUrl = ref(helpCenterUrl(null));
const frameKey = ref(0);
const isContactOpen = ref(false);
const closeButton = ref<HTMLButtonElement>();
const dialog = ref<HTMLElement>();
// Where focus was before opening (the ? button), so closing can put it back.
let returnFocusTo: HTMLElement | null = null;

function showContextPage(): void {
  pageUrl.value = helpCenterUrl(helpContext());
  frameKey.value += 1;
}

// The drawer is modal: keep the page behind it from scrolling while it's open.
function lockPageScroll(locked: boolean): void {
  document.documentElement.style.overflow = locked ? 'hidden' : '';
}

// On the document rather than the drawer, so Escape still closes it when focus has
// fallen back to <body> (e.g. after the Still stuck? button is replaced by the form).
function closeOnEscape(event: KeyboardEvent): void {
  if (event.key === 'Escape') isOpen.value = false;
}

// Immediate, so a drawer that's already open when this mounts (after a layout change)
// still shows the current screen's page.
watch(
  isOpen,
  async (open) => {
    lockPageScroll(open);
    if (open) {
      document.addEventListener('keydown', closeOnEscape);
      returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      showContextPage();
      await nextTick();
      closeButton.value?.focus();
    } else {
      document.removeEventListener('keydown', closeOnEscape);
      isContactOpen.value = false;
      returnFocusTo?.focus();
      returnFocusTo = null;
    }
  },
  { immediate: true },
);

// Still stuck? removes the focused button; keep focus in the drawer instead of <body>.
watch(isContactOpen, async (contactOpen) => {
  if (!contactOpen) return;
  await nextTick();
  dialog.value?.focus();
});

onBeforeUnmount(() => {
  lockPageScroll(false);
  document.removeEventListener('keydown', closeOnEscape);
});
</script>

<template>
  <v-navigation-drawer
    v-model="isOpen"
    temporary
    touchless
    location="right"
    width="480"
    class="help-drawer"
    data-test="help-drawer"
  >
    <div
      ref="dialog"
      class="flex h-full flex-col outline-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-drawer-title"
      tabindex="-1"
    >
      <div class="flex items-center gap-3 border-b border-tint-40 px-4 py-3">
        <span id="help-drawer-title" class="text-base font-bold text-shade-900">Help</span>
        <button
          type="button"
          class="ml-auto text-sm font-semibold text-highlight-600 hover:underline"
          data-test="help-drawer-home"
          @click="showContextPage"
        >
          Help home
        </button>
        <a
          :href="pageUrl"
          target="_blank"
          rel="noopener"
          class="text-sm font-semibold text-highlight-600 hover:underline"
          data-test="help-drawer-new-tab"
        >
          Open help center ↗
        </a>
        <button
          ref="closeButton"
          type="button"
          class="grid h-8 w-8 place-content-center rounded-md hover:bg-tint-40"
          aria-label="Close help"
          data-test="help-drawer-close"
          @click="isOpen = false"
        >
          <CloseIcon class="h-5 w-5" />
        </button>
      </div>

      <iframe
        v-if="isOpen"
        :key="frameKey"
        :src="pageUrl"
        title="FactoryFix help center"
        class="min-h-0 w-full flex-1 border-0"
        data-test="help-drawer-frame"
      />

      <div class="border-t border-tint-40">
        <button
          v-if="!isContactOpen"
          type="button"
          class="flex w-full items-center justify-between px-4 py-3 text-sm hover:bg-tint-20"
          data-test="help-drawer-contact"
          @click="isContactOpen = true"
        >
          <span class="text-shade-800">Still stuck?</span>
          <span class="font-semibold text-highlight-600">Contact support</span>
        </button>
        <GetHelpContent v-else />
      </div>
    </div>
  </v-navigation-drawer>
</template>
